// Add to Home Screen functionality for mobile devices
// Instructions are automatically adapted based on device type (iOS vs Android)
// iOS: Shows Safari-specific instructions (Share button → Add to Home Screen)
// Android: Shows browser menu instructions (three dots → Add to Home Screen)

/**
 * Check if device is mobile
 */
function isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.matchMedia('(max-device-width: 800px)').matches ||
           window.innerWidth <= 800;
}

/**
 * Detect if device is iOS (iPhone, iPad, iPod)
 */
function isIOSDevice() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
           (navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
}

/**
 * Detect if device is Android
 */
function isAndroidDevice() {
    return /Android/i.test(navigator.userAgent);
}

/**
 * Check if app is running in standalone mode (added to home screen)
 * This function detects when the app is launched from a home screen shortcut
 */
function isStandaloneMode() {
    // Method 1: Check for standalone display mode (most reliable for PWA)
    // Works for Android Chrome, iOS Safari, and other modern browsers
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }
    
    // Method 2: Check for iOS standalone mode (Safari specific)
    // navigator.standalone is true when running in standalone mode on iOS
    // This is the primary way to detect iOS home screen apps
    if (window.navigator && window.navigator.standalone === true) {
        return true;
    }
    
    // Method 3: Check URL parameter (if opened via homescreen link with parameter)
    // This allows manual testing and explicit detection
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('from_homescreen') === '1') {
        return true;
    }
    
    // Method 4: Check if running in fullscreen mode (some Android browsers)
    // When app is added to home screen, it often runs in fullscreen
    // But we need to be careful not to confuse with regular fullscreen API
    if (window.matchMedia) {
        const fullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
        // Only consider it standalone if it's fullscreen AND we're on mobile
        // AND there's no browser UI (which we can't directly detect, so we use heuristics)
        if (fullscreen && isMobileDevice()) {
            // Additional check: if window height matches screen height (no browser chrome)
            // This is a heuristic and not 100% reliable, but helps catch edge cases
            const windowHeight = window.innerHeight;
            const screenHeight = window.screen.height;
            // If window height is very close to screen height, likely standalone
            if (Math.abs(windowHeight - screenHeight) < 50) {
                return true;
            }
        }
    }
    
    return false;
}

/**
 * Check if browser supports Add to Home Screen
 */
function supportsAddToHomeScreen() {
    // Check for beforeinstallprompt event (Android Chrome)
    // This will be set by the event listener if available
    return typeof window.deferredPrompt !== 'undefined' || 
           isMobileDevice(); // iOS Safari always supports it
}

/**
 * Show Add to Home Screen prompt
 */
function showAddToHomeScreenPrompt() {
    // Android Chrome - use beforeinstallprompt event
    if (window.deferredPrompt) {
        window.deferredPrompt.prompt();
        window.deferredPrompt.userChoice.then((choiceResult) => {
            if (choiceResult.outcome === 'accepted') {
                console.log('[AddToHomeScreen] User accepted the prompt');
                // After installation, the app will open in standalone mode
                // The button will be hidden automatically by isStandaloneMode() check
            } else {
                console.log('[AddToHomeScreen] User dismissed the prompt');
            }
            window.deferredPrompt = null;
        });
        return;
    }
    
    // Show platform-specific instructions
    if (isIOSDevice()) {
        showIOSInstructions();
    } else if (isAndroidDevice()) {
        showAndroidInstructions();
    } else {
        // Fallback: try to detect from user agent or show generic instructions
        // Default to iOS instructions for unknown mobile devices (most likely iOS)
        if (isMobileDevice()) {
            showIOSInstructions();
        }
    }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Get translation for a key
 * Uses LanguageManager.translate() if available, falls back to legacy method
 */
function getTranslation(key) {
    // Use LanguageManager if available (new translation system)
    if (window.LanguageManager && typeof window.LanguageManager.translate === 'function') {
        try {
            const translation = window.LanguageManager.translate(key);
            if (translation !== key) {
                return translation;
            }
        } catch (e) {
            console.debug('[AddToHomeScreen] LanguageManager.translate error, falling back:', e);
        }
    }
    
    // Use TranslationRegistry if available
    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
        try {
            const lang = (typeof window.LANG !== 'undefined' ? window.LANG : 'EN').toUpperCase();
            const translation = window.TranslationRegistry.translate(key, lang);
            if (translation !== key) {
                return translation;
            }
        } catch (e) {
            console.debug('[AddToHomeScreen] TranslationRegistry.translate error, falling back:', e);
        }
    }
    
    // Use TranslationRegistry
    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
        const lang = (typeof window.LANG !== 'undefined' ? window.LANG : 'EN').toUpperCase();
        const translation = window.TranslationRegistry.translate(key, lang);
        if (translation !== key) {
            return translation;
        }
    }
    
    return key;
}

/**
 * Show iOS instructions for adding to home screen
 */
function showIOSInstructions() {
    const instructions = `
        <div class="add-to-homescreen-instructions">
            <div class="add-to-homescreen-instructions__content">
                <h5 data-translate="add_to_homescreen_title">Add to Home Screen</h5>
                <ol>
                    <li><span data-translate="add_to_homescreen_ios_step1">Tap the</span> <strong data-translate="add_to_homescreen_ios_step2">Share</strong> <i class="bi bi-share"></i></li>
                    <li><span data-translate="add_to_homescreen_ios_step3">Scroll down and select</span> <strong data-translate="add_to_homescreen_ios_step4">Add to Home Screen</strong></li>
                    <li><span data-translate="add_to_homescreen_ios_step5">Tap</span> <strong data-translate="add_to_homescreen_ios_step6">Add</strong></li>
                </ol>
                <button class="btn btn-primary btn-sm" data-close-instructions data-translate="add_to_homescreen_instructions_close">Close</button>
            </div>
        </div>
    `;
    
    $('body').append(instructions);
    const $instructions = $('.add-to-homescreen-instructions');
    
    // Apply translations after DOM insertion
    if (window.TranslationApplier && typeof window.TranslationApplier.apply === 'function') {
        window.TranslationApplier.apply();
    }
    
    setTimeout(() => {
        $instructions.addClass('add-to-homescreen-instructions--visible');
    }, 10);
    
    $instructions.find('[data-close-instructions]').on('click', function() {
        $instructions.removeClass('add-to-homescreen-instructions--visible');
        setTimeout(() => {
            $instructions.remove();
        }, 300);
    });
    
    $instructions.on('click', function(e) {
        if ($(e.target).hasClass('add-to-homescreen-instructions')) {
            $instructions.removeClass('add-to-homescreen-instructions--visible');
            setTimeout(() => {
                $instructions.remove();
            }, 300);
        }
    });
}

/**
 * Show Android instructions for adding to home screen
 */
function showAndroidInstructions() {
    const instructions = `
        <div class="add-to-homescreen-instructions">
            <div class="add-to-homescreen-instructions__content">
                <h5 data-translate="add_to_homescreen_title">Add to Home Screen</h5>
                <ol>
                    <li data-translate="add_to_homescreen_android_step1">Open browser menu (three dots)</li>
                    <li><span data-translate="add_to_homescreen_android_step2">Select</span> <strong data-translate="add_to_homescreen_android_step3">Add to Home Screen</strong></li>
                    <li data-translate="add_to_homescreen_android_step4">Confirm the addition</li>
                </ol>
                <button class="btn btn-primary btn-sm" data-close-instructions data-translate="add_to_homescreen_instructions_close">Close</button>
            </div>
        </div>
    `;
    
    $('body').append(instructions);
    const $instructions = $('.add-to-homescreen-instructions');
    
    // Apply translations after DOM insertion
    if (window.TranslationApplier && typeof window.TranslationApplier.apply === 'function') {
        window.TranslationApplier.apply();
    }
    
    setTimeout(() => {
        $instructions.addClass('add-to-homescreen-instructions--visible');
    }, 10);
    
    $instructions.find('[data-close-instructions]').on('click', function() {
        $instructions.removeClass('add-to-homescreen-instructions--visible');
        setTimeout(() => {
            $instructions.remove();
        }, 300);
    });
    
    $instructions.on('click', function(e) {
        if ($(e.target).hasClass('add-to-homescreen-instructions')) {
            $instructions.removeClass('add-to-homescreen-instructions--visible');
            setTimeout(() => {
                $instructions.remove();
            }, 300);
        }
    });
}

/**
 * Update button visibility based on current mode
 */
function updateButtonVisibility() {
    if (typeof $ === 'undefined' || typeof jQuery === 'undefined') {
        return;
    }
    
    const $button = $('[data-add-to-homescreen]');
    if ($button.length === 0) {
        return;
    }
    
    const $navItem = $button.closest('.nav-item');
    
    // Hide button if in standalone mode
    if (isStandaloneMode()) {
        $navItem.hide();
        return;
    }
    
    // Only show on mobile devices
    if (!isMobileDevice()) {
        $navItem.hide();
        return;
    }
    
    // Show button if conditions are met
    $navItem.show();
}

/**
 * Initialize Add to Home Screen functionality
 */
export function initializeAddToHomeScreen() {
    // Wait for jQuery to be available
    if (typeof $ === 'undefined' || typeof jQuery === 'undefined') {
        // Retry after a short delay
        setTimeout(() => {
            initializeAddToHomeScreen();
        }, 100);
        return;
    }
    
    const $button = $('[data-add-to-homescreen]');
    if ($button.length === 0) {
        return; // Button not found in DOM
    }
    
    const $navItem = $button.closest('.nav-item');
    
    // Initial check: hide button if already in standalone mode
    if (isStandaloneMode()) {
        $navItem.hide();
        // Still set up listeners in case mode changes
    }
    
    // Listen for display-mode changes (when app switches to/from standalone)
    if (window.matchMedia) {
        const standaloneQuery = window.matchMedia('(display-mode: standalone)');
        
        // Check immediately
        if (standaloneQuery.matches) {
            $navItem.hide();
        }
        
        // Listen for changes (some browsers support this)
        if (standaloneQuery.addEventListener) {
            standaloneQuery.addEventListener('change', (e) => {
                updateButtonVisibility();
            });
        } else if (standaloneQuery.addListener) {
            // Fallback for older browsers
            standaloneQuery.addListener((e) => {
                updateButtonVisibility();
            });
        }
    }
    
    // Only show on mobile devices and if not in standalone mode
    if (!isMobileDevice() || isStandaloneMode()) {
        $navItem.hide();
    } else {
        $navItem.show();
    }
    
    // Listen for beforeinstallprompt event (Android Chrome)
    window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent the default prompt
        e.preventDefault();
        // Store the event for later use
        window.deferredPrompt = e;
        
        // Only show button if not in standalone mode
        if (!isStandaloneMode() && isMobileDevice()) {
            $navItem.show();
        }
    });
    
    // Handle click on Add to Home Screen button
    $button.off('click.add-to-homescreen').on('click.add-to-homescreen', function(e) {
        e.preventDefault();
        showAddToHomeScreenPrompt();
    });
    
    // If opened via homescreen link, redirect to clean URL and hide button
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('from_homescreen') === '1') {
        // Remove parameter from URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete('from_homescreen');
        window.history.replaceState({}, '', url.toString());
        
        // Hide button (we're in standalone mode)
        $navItem.hide();
    }
    
    // Periodic check for standalone mode (in case detection was delayed)
    // This helps catch cases where the initial check might have missed it
    setTimeout(() => {
        updateButtonVisibility();
    }, 500);
    
    // Also check on window focus (user might have switched apps)
    window.addEventListener('focus', () => {
        updateButtonVisibility();
    });
}


